/**
 * GhostCheckSection · iter170 (2026-02-27)
 *
 * Admin-UI zur Anzeige und Bereinigung von Ghost-References
 * (Orphaned Referenzen auf gelöschte User/Listings/Posts in
 * `watchlist`, `cart_items`, `notifications`, `follows`, …).
 *
 * Nutzt Backend:
 *   GET  /api/admin/ghost-check
 *   POST /api/admin/ghost-check/purge
 */
import { useState, useEffect, useCallback } from "react";
import api, { formatError } from "@/lib/api";
import { Ghost, RefreshCw, Trash2, AlertTriangle, CheckCircle2, Eye } from "lucide-react";

export default function GhostCheckSection() {
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null); // {ghosts:{}, total_ghost_refs}
  const [lastPurge, setLastPurge] = useState(null);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [dryRunPreview, setDryRunPreview] = useState(null); // {detail}
  const [runningDry, setRunningDry] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/ghost-check");
      setReport(data);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doDryRun = async () => {
    setRunningDry(true);
    setError("");
    setDryRunPreview(null);
    try {
      const { data } = await api.post("/admin/ghost-check/purge?dry_run=true");
      setDryRunPreview(data);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setRunningDry(false);
    }
  };

  const doPurge = async () => {
    setPurging(true);
    setError("");
    try {
      const { data } = await api.post("/admin/ghost-check/purge");
      setLastPurge(data);
      setConfirmPurge(false);
      setDryRunPreview(null);
      await load();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setPurging(false);
    }
  };

  const ghosts = report?.ghosts || {};
  const total = report?.total_ghost_refs || 0;
  const groups = groupGhosts(ghosts);

  return (
    <div data-testid="ghost-check-section" className="max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Ghost size={22} className="text-[#0A0A0A]" />
          <div>
            <h2 className="text-lg font-bold text-[#0A0A0A]">Ghost-Check</h2>
            <p className="text-xs text-[#666]">
              Findet Verweise auf nicht mehr existierende User, Artikel oder Beiträge.
            </p>
          </div>
        </div>
        <button
          data-testid="ghost-check-refresh"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#E5E5E5] text-sm hover:bg-[#F5F5F5] transition disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Neu prüfen
        </button>
      </div>

      {error && (
        <div
          data-testid="ghost-check-error"
          className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 flex items-start gap-2"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {lastPurge && (
        <div
          data-testid="ghost-check-purge-success"
          className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800 flex items-start gap-2"
        >
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <span>Bereinigt · {lastPurge.report?.totals?.deleted ?? 0} Referenzen entfernt.</span>
        </div>
      )}

      {/* Total */}
      <div
        data-testid="ghost-check-total"
        className={`rounded-2xl border p-6 mb-4 ${
          total > 0
            ? "border-amber-200 bg-amber-50"
            : "border-emerald-200 bg-emerald-50"
        }`}
      >
        <div className="text-xs uppercase tracking-wider text-[#666] mb-1">
          Gesamte Ghost-Referenzen
        </div>
        <div className={`text-3xl font-bold ${total > 0 ? "text-amber-900" : "text-emerald-900"}`}>
          {loading ? "…" : total}
        </div>
        {total === 0 && !loading && (
          <div className="text-xs text-emerald-800 mt-2">Datenbank ist sauber – keine verwaisten Referenzen.</div>
        )}
      </div>

      {/* Grouped Details */}
      {total > 0 && (
        <>
          <div className="space-y-4 mb-6">
            {Object.entries(groups).map(([groupKey, rows]) => (
              <div
                key={groupKey}
                data-testid={`ghost-group-${groupKey}`}
                className="rounded-2xl border border-[#E5E5E5] bg-white overflow-hidden"
              >
                <div className="px-4 py-2 bg-[#F5F5F5] border-b border-[#E5E5E5]">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#666]">
                    {groupLabel(groupKey)}
                  </div>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#999]">
                      <th className="text-left px-4 py-2 font-semibold">Collection</th>
                      <th className="text-left px-4 py-2 font-semibold">Feld</th>
                      <th className="text-right px-4 py-2 font-semibold">Anzahl</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ key, collection, field, count }) => (
                      <tr key={key} data-testid={`ghost-row-${key}`} className="border-t border-[#F0F0F0]">
                        <td className="px-4 py-2 text-sm font-medium">{collection}</td>
                        <td className="px-4 py-2 text-sm text-[#666]">{field}</td>
                        <td className="px-4 py-2 text-right text-sm font-bold text-amber-800">
                          {count.toLocaleString("de-DE")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* Purge CTA */}
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-red-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-bold text-red-900 mb-1">Verwaiste Referenzen bereinigen</div>
                <p className="text-xs text-red-800 mb-3">
                  Alle {total.toLocaleString("de-DE")} Ghost-Referenzen werden endgültig
                  entfernt. Verkaufte Artikel (§ 147 AO / DSGVO) bleiben unberührt.
                  Nutze zuerst <strong>Vorschau (Dry-Run)</strong> um zu prüfen, was gelöscht würde.
                </p>

                {dryRunPreview && (
                  <div
                    data-testid="ghost-check-dry-run-preview"
                    className="mb-3 rounded-xl bg-white border border-red-200 px-3 py-2 text-xs text-[#0A0A0A]"
                  >
                    <div className="font-semibold text-[#0A0A0A] mb-2 flex items-center gap-1.5">
                      <Eye size={12} /> Vorschau (Dry-Run) · {(dryRunPreview.report?.totals?.would_delete ?? 0).toLocaleString("de-DE")} Referenzen würden gelöscht
                    </div>
                    {["listing_refs", "post_refs", "user_refs"].map((k) => {
                      const rows = dryRunPreview.report?.[k] || [];
                      if (!rows.length) return null;
                      const label = { listing_refs: "Artikel-Verweise", post_refs: "Beitrags-Verweise", user_refs: "User-Verweise" }[k];
                      return (
                        <div key={k} className="mb-2">
                          <div className="text-[10px] uppercase tracking-wider text-[#666] font-bold mb-1">{label}</div>
                          {rows.map((r) => (
                            <div key={`${r.collection}.${r.field}`} className="text-[11px] mb-1 pl-2 border-l-2 border-red-200">
                              <span className="font-semibold">{r.collection}</span>.<span className="text-[#666]">{r.field}</span>{" "}
                              <span className="text-red-700">→ {r.count} Referenzen</span>
                              {r.samples?.length > 0 && (
                                <div className="text-[10px] text-[#999] mt-0.5 font-mono">
                                  Beispiele: {r.samples.slice(0, 3).map((s) => String(s).slice(0, 20)).join(", ")}
                                  {r.samples.length > 3 ? "…" : ""}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid="ghost-check-dry-run-btn"
                    onClick={doDryRun}
                    disabled={runningDry || purging}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0A0A0A] text-[#0A0A0A] text-sm font-medium hover:bg-[#F5F5F5] transition disabled:opacity-50"
                  >
                    {runningDry ? <RefreshCw size={14} className="animate-spin" /> : <Eye size={14} />}
                    Vorschau (Dry-Run)
                  </button>

                  {!confirmPurge ? (
                    <button
                      data-testid="ghost-check-purge-btn"
                      onClick={() => setConfirmPurge(true)}
                      disabled={purging}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-700 text-white text-sm font-medium hover:bg-red-800 transition disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                      Jetzt bereinigen …
                    </button>
                  ) : (
                    <>
                      <button
                        data-testid="ghost-check-purge-confirm"
                        onClick={doPurge}
                        disabled={purging}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-700 text-white text-sm font-bold hover:bg-red-800 transition disabled:opacity-50"
                      >
                        {purging ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        Ja, endgültig bereinigen
                      </button>
                      <button
                        data-testid="ghost-check-purge-cancel"
                        onClick={() => setConfirmPurge(false)}
                        disabled={purging}
                        className="px-4 py-2 rounded-full border border-[#E5E5E5] text-sm hover:bg-white transition disabled:opacity-50"
                      >
                        Abbrechen
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Gruppiert `collection.field: count` in {LISTING|POST|USER}
function groupGhosts(ghosts) {
  const groups = { LISTING: [], POST: [], USER: [] };
  Object.entries(ghosts).forEach(([key, count]) => {
    const [collection, field] = key.split(".");
    const entry = { key, collection, field, count };
    if (field === "listing_id") groups.LISTING.push(entry);
    else if (field === "post_id") groups.POST.push(entry);
    else groups.USER.push(entry);
  });
  // leere Gruppen entfernen
  Object.keys(groups).forEach((k) => {
    if (groups[k].length === 0) delete groups[k];
  });
  return groups;
}

function groupLabel(key) {
  return (
    {
      LISTING: "Verweise auf gelöschte Artikel",
      POST: "Verweise auf gelöschte Beiträge",
      USER: "Verweise auf gelöschte User",
    }[key] || key
  );
}
